/**
 * Contesto automatico per le segnalazioni.
 *
 * Metà delle segnalazioni si risolve sapendo versione, schermata e ultimo
 * errore: chiederle all'utente in cantiere non funziona, quindi le raccogliamo
 * da soli. Il buffer errori va installato all'avvio (src/index.tsx) perché
 * l'errore utile e' quasi sempre avvenuto PRIMA che l'utente aprisse la scheda.
 *
 * Non raccogliamo dati di cantiere: nessun contenuto di progetti, mappature o
 * foto finisce qui dentro. Solo diagnostica tecnica.
 */

import { APP_VERSION } from '../appChangelog';

/** Quanti errori recenti conservare. Il server ne accetta comunque max 10. */
const MAX_ERRORI = 10;
/** Taglio per singolo messaggio: evita che uno stack enorme riempia tutto. */
const MAX_LUNGHEZZA_ERRORE = 500;

const bufferErrori: string[] = [];
let installato = false;

function registraErrore(origine: string, dettaglio: unknown): void {
  let testo: string;
  try {
    if (dettaglio instanceof Error) {
      testo = `${dettaglio.name}: ${dettaglio.message}`;
    } else if (typeof dettaglio === 'string') {
      testo = dettaglio;
    } else {
      testo = JSON.stringify(dettaglio);
    }
  } catch {
    testo = String(dettaglio);
  }

  const ora = new Date().toISOString().slice(11, 19);
  bufferErrori.push(`[${ora}] ${origine}: ${testo}`.slice(0, MAX_LUNGHEZZA_ERRORE));
  while (bufferErrori.length > MAX_ERRORI) bufferErrori.shift();
}

/**
 * Installa i listener globali. Idempotente: chiamarla piu' volte non duplica
 * la cattura (in StrictMode il modulo puo' essere valutato due volte).
 */
export function installaCatturaErrori(): void {
  if (installato) return;
  installato = true;

  window.addEventListener('error', (ev) => {
    registraErrore('errore', ev.message || ev.error);
  });

  window.addEventListener('unhandledrejection', (ev) => {
    registraErrore('promise', ev.reason);
  });

  // console.error resta funzionante: lo avvolgiamo, non lo sostituiamo.
  const originale = console.error;
  console.error = (...args: unknown[]) => {
    registraErrore('console', args.map((a) => (a instanceof Error ? a.message : a)).join(' '));
    originale.apply(console, args as []);
  };
}

/** Copia degli errori recenti, dal piu' vecchio al piu' recente. */
export function erroriRecenti(): string[] {
  return [...bufferErrori];
}

export interface ContestoSegnalazione {
  appVersion: string;
  view: string;
  userAgent: string;
  viewport: string;
  online: boolean;
  language: string;
  recentErrors: string[];
}

/**
 * Fotografia dello stato al momento dell'invio.
 * `view` e' la schermata da cui l'utente ha aperto la segnalazione.
 */
export function raccogliContesto(view: string): ContestoSegnalazione {
  return {
    appVersion: APP_VERSION,
    view,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    online: navigator.onLine,
    language: navigator.language,
    recentErrors: erroriRecenti(),
  };
}
